import {Button, Flag, IconButton, Separator, copyToClipboard} from 'platform/components';
import {Box, HStack, Text, VStack, Show} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {ServiceAccountKeyResponseBody} from '@dms/api/security';
import i18n from '@dms/i18n';

import {parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {getServiceAccountKeyStatusFlagProps} from '../utils/getServiceAccountKeyStatusFlagProps';

interface ServiceAccountKeyCardProps extends RequiredTestIdProps {
  serviceAccountKey: ServiceAccountKeyResponseBody;
  canRevoke: boolean;
  onRevoke: () => void;
}

export function ServiceAccountKeyCard(props: ServiceAccountKeyCardProps) {
  const formatDateTime = useDateTimeFormatter();

  const serviceAccountKey = props.serviceAccountKey;
  const publicKeyLines = serviceAccountKey.publicKeyPem.split('\n');
  const isExpired = serviceAccountKey.status === 'expired';
  const isRotating = serviceAccountKey.status === 'rotating';
  const canRevoke = props.canRevoke && !isExpired;

  return (
    <Box
      border="1px solid"
      borderColor="general.separator"
      borderRadius="small"
      padding={4}
      data-testid={suffixTestId('card', props)}
    >
      <VStack spacing={3}>
        <HStack spacing={3} align="center">
          <Text size="small" data-testid={suffixTestId('keyId', props)}>
            {serviceAccountKey.id}
          </Text>
          <Flag
            {...getServiceAccountKeyStatusFlagProps(serviceAccountKey.status)}
            data-testid={suffixTestId('status', props)}
          />
          <Box flex={1} />
          <IconButton
            icon="content/copy"
            priority="secondary"
            size="small"
            onClick={() => copyToClipboard(serviceAccountKey.id)}
            aria-label={i18n.t('general.actions.copyToClipboard')}
            data-testid={suffixTestId('copyKeyId', props)}
          />
          <Show when={canRevoke}>
            <Button
              variant="dangerLink"
              size="small"
              onClick={props.onRevoke}
              title={i18n.t('entity.serviceAccount.actions.revokeKey')}
              data-testid={suffixTestId('revoke', props)}
            />
          </Show>
        </HStack>

        <HStack spacing={6} wrap>
          <Text size="xSmall" color="secondary">
            {`${i18n.t('entity.serviceAccount.labels.keyCreatedAt')}: ${formatDateTime('dateTimeShort', parseDate(serviceAccountKey.createdAt))}`}
          </Text>
          <Show when={isRotating && serviceAccountKey.rotatingUntil}>
            {(rotatingUntil) => (
              <Text size="xSmall" color="secondary">
                {`${i18n.t('entity.serviceAccount.labels.keyValidUntil')}: ${formatDateTime('dateTimeShort', parseDate(rotatingUntil))}`}
              </Text>
            )}
          </Show>
          <Text size="xSmall" color="secondary">
            {i18n.t('entity.serviceAccount.labels.keyAlgorithm')}
          </Text>
        </HStack>

        <Separator spacing={0} />

        <VStack spacing={1}>
          <HStack spacing={2} align="center" justify="space-between">
            <Text size="xSmall" color="secondary">
              {i18n.t('entity.serviceAccount.labels.publicKey')}
            </Text>
            <IconButton
              icon="content/copy"
              priority="secondary"
              size="small"
              onClick={() => copyToClipboard(serviceAccountKey.publicKeyPem)}
              aria-label={i18n.t('general.actions.copyToClipboard')}
              data-testid={suffixTestId('copyPublicKey', props)}
            />
          </HStack>
          <Box backgroundColor="palettes.neutral.20.100" borderRadius="small" padding={3}>
            <VStack spacing={0}>
              {publicKeyLines.map((line, index) => (
                // eslint-disable-next-line react/no-array-index-key -- Pre-existing violation
                <Text key={`${index}-${line}`} size="xSmall">
                  {line}
                </Text>
              ))}
            </VStack>
          </Box>
        </VStack>
      </VStack>
    </Box>
  );
}
